import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box } from '@mui/material'
import React, { useState } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { NumberField } from '@e3dc-react/shell/Elements/InputFields'
import { Meter } from '../../types'
import { getIsDuplicatePairingId, isWithinBounds } from '../../utils/pairing-id'
import { useDialogActions } from '../../../components/Dialogs/hooks'

//#region Dialog
export interface MeterPairingDialogProps {
  meter: Meter
  meters: Meter[]
  htc320hIoPairingId: string | undefined
  submitPairingId: (id: string) => void
  onClose?: () => void
}

/** Dialog for pairing a meter with a pairing ID. */
export const MeterPairingDialog: React.FC<MeterPairingDialogProps> = ({ meter, meters, htc320hIoPairingId, submitPairingId, onClose }) => {
  const { t } = useCustomTranslation()

  const initialPairingId = Number(meter.pairingId)

  const [pairingId, setPairingId] = useState(initialPairingId)

  const isOutOfBounds = !isWithinBounds(pairingId)
  const isDuplicatePairingId = getIsDuplicatePairingId(meter.id, pairingId, meters, htc320hIoPairingId)

  const errorKey = getErrorKey(isOutOfBounds, isDuplicatePairingId)
  const error = errorKey ? t(errorKey) : undefined

  const isSame = initialPairingId === pairingId
  const isPrimaryDisabled = !!error || isSame

  const handleClose = (): void => {
    onClose?.()
  }

  const handleSubmit = (): void => {
    submitPairingId(pairingId.toString())

    // TODO: if device exists, pair and save (replace if a previous id exists).
    // If not, just set the pairingId and handle it on installation save.
    // Reference functions
    // handlePairingSave, ConfigurationEditor:1452
    // onReplaceDevice, ConfigurationEditor:1052

    onClose?.()
  }

  const primaryName = meter.isNew ? t('installation.productEditor.pairingEditor.save') : t('installation.productEditor.pairingEditor.pairAndSave')
  const dialogActions = useDialogActions({
    primary: {
      name: primaryName,
      onClick: handleSubmit,
      disabled: isPrimaryDisabled,
    },
    secondary: { onClick: handleClose },
  })

  return (
    <Dialog title={t('installation.productEditor.pairingEditor.title.pvextern')} onClose={handleClose} maxWidth="md" actions={dialogActions}>
      <Box sx={{ pt: 2 }}>
        <NumberField
          name="meter pairing id"
          label={t('productEditor.pairingEditor.pairingID.submeter')}
          value={pairingId}
          onChange={value => setPairingId(value || 0)}
          min={0}
          max={250}
          error={!!error}
          helperText={error}
        />
      </Box>
    </Dialog>
  )
}
//#endregion

//#region Helpers
const getErrorKey = (isOutOfBounds: boolean, isDuplicatePairingId: boolean): string | undefined => {
  if (isOutOfBounds) return 'installation.productEditor.pairingEditor.submeter-id-out-of-bounds'
  if (isDuplicatePairingId) return 'installation.productEditor.pairingEditor.notAllowedId'
  return undefined
}
